import { Warning } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";

/**
 * One shape for all six confirm cards: title · meta line · optional preview · zero or more
 * warnings · primary + Discard.
 *
 * `canCreate = missing.length === 0` — the primary is disabled whenever the server reported any
 * gap, and every gap has a sentence beside it. A disabled button with no explanation is the
 * defect this card exists to avoid.
 */
export interface IntentCardProps {
  title: string;
  meta: string;
  preview?: string | null;
  warnings?: string[];
  /** Rendered under the first warning — e.g. "Open Task Center" for a missing digest target. */
  warningAction?: ReactNode;
  primaryLabel: string;
  canCreate: boolean;
  busy?: boolean;
  onPrimary: () => void;
  onDiscard: () => void;
  children?: ReactNode;
}

export function IntentCard({
  title,
  meta,
  preview,
  warnings = [],
  warningAction,
  primaryLabel,
  canCreate,
  busy = false,
  onPrimary,
  onDiscard,
  children,
}: IntentCardProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 border-t border-divider p-3">
      <Text variant="body" className="font-semibold">
        {title}
      </Text>
      <Text variant="small">{meta}</Text>

      {preview ? (
        <pre className="scrollbar-thin max-h-40 overflow-y-auto whitespace-pre-wrap rounded-sm bg-bg-secondary p-2 text-small text-text-secondary">
          {preview}
        </pre>
      ) : null}

      {warnings.map((warning) => (
        <p key={warning} className="flex items-start gap-1.5 text-small text-danger">
          <Warning size={14} aria-hidden className="mt-0.5 shrink-0" />
          {warning}
        </p>
      ))}
      {warnings.length > 0 && warningAction ? warningAction : null}

      {children}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDiscard}>
          {t("Discard")}
        </Button>
        <Button variant="primary" size="sm" disabled={!canCreate || busy} onClick={onPrimary}>
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
}
